import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Trash2, Plus } from 'lucide-react';
import { CalculatedVariable, Rule, Condition } from './types';

interface CalculatedVariablesModalProps {
  visible: boolean;
  onHide: () => void;
  onSave: (variable: CalculatedVariable) => void;
}

export const CalculatedVariablesModal: React.FC<CalculatedVariablesModalProps> = ({
  visible,
  onHide,
  onSave
}) => {
  const [name, setName] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [rules, setRules] = useState<Rule[]>([]);

  const operators = [
    { label: 'Eşittir (==)', value: 'equals' },
    { label: 'Eşit Değildir (!=)', value: 'notEquals' },
    { label: 'İle Başlar', value: 'startsWith' },
    { label: 'İçerir', value: 'contains' },
    { label: 'Boştur', value: 'isEmpty' },
    { label: 'SKU İçerir (Liste)', value: 'hasItemWithSku' }
  ];

  const handleAddRule = () => {
    setRules([...rules, {
      conditions: [{ field: '', operator: 'equals', value: '' }],
      conditionLogic: 'AND',
      resultValue: ''
    }]);
  };

  const handleAddCondition = (ruleIndex: number) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions.push({ field: '', operator: 'equals', value: '' });
    setRules(newRules);
  };

  const handleUpdateCondition = (ruleIndex: number, condIndex: number, updates: Partial<Condition>) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions[condIndex] = { ...newRules[ruleIndex].conditions[condIndex], ...updates };
    setRules(newRules);
  };

  const handleRemoveCondition = (ruleIndex: number, condIndex: number) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions = newRules[ruleIndex].conditions.filter((_, i) => i !== condIndex);
    setRules(newRules);
  };

  const handleRemoveRule = (ruleIndex: number) => {
    setRules(rules.filter((_, i) => i !== ruleIndex));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      id: Math.random().toString(36).substr(2, 9),
      name,
      rules,
      defaultValue
    });
    onHide();
  };

  return (
    <Dialog header="Koşullu Değişken Oluştur (Rules Engine)" visible={visible} style={{ width: '800px' }} onHide={onHide}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="İptal" icon="pi pi-times" onClick={onHide} className="p-button-text" />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} autoFocus />
        </div>
      }>
      
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-2">
            <label className="text-sm font-semibold text-text">Değişken Adı</label>
            <InputText placeholder="Örn: calculatedCargo" value={name} onChange={(e) => setName(e.target.value)} className="p-inputtext-sm" />
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <label className="text-sm font-semibold text-text">Varsayılan Değer (Fallback)</label>
            <InputText placeholder="Örn: GEL AL" value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} className="p-inputtext-sm" />
          </div>
        </div>

        <div className="border-t border-borderColor pt-4 mt-2">
          <h3 className="text-sm font-bold text-text mb-2">Kurallar (Sırayla İşlenir)</h3>
          
          {rules.map((rule, rIndex) => (
            <div key={rIndex} className="bg-bgAlt border border-borderColor rounded p-3 mb-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-text/50">KURAL #{rIndex + 1}</span>
                <Button icon={<Trash2 className="w-4 h-4" />} className="p-button-danger p-button-text p-1 text-danger hover:bg-danger/10" onClick={() => handleRemoveRule(rIndex)} />
              </div>
              
              <div className="flex flex-col gap-2 mb-3 pl-2 border-l-2 border-primary/30">
                {rule.conditions.map((cond, cIndex) => (
                  <div key={cIndex} className="flex items-center gap-2">
                    {cIndex > 0 && <span className="text-xs font-bold text-primary w-8">{rule.conditionLogic}</span>}
                    <InputText placeholder="Alan (örn: order.shipment)" value={cond.field} onChange={(e) => handleUpdateCondition(rIndex, cIndex, { field: e.target.value })} className="p-inputtext-sm flex-1" />
                    <Dropdown value={cond.operator} options={operators} onChange={(e) => handleUpdateCondition(rIndex, cIndex, { operator: e.value })} className="p-inputtext-sm w-40" />
                    {cond.operator !== 'isEmpty' && cond.operator !== 'isNotEmpty' && (
                       <InputText placeholder="Değer" value={cond.value || ''} onChange={(e) => handleUpdateCondition(rIndex, cIndex, { value: e.target.value })} className="p-inputtext-sm flex-1" />
                    )}
                    <Button icon={<Trash2 className="w-4 h-4"/>} className="p-button-danger p-button-text p-1 text-danger hover:bg-danger/10" onClick={() => handleRemoveCondition(rIndex, cIndex)} />
                  </div>
                ))}
                
                <Button label="Koşul Ekle (AND/OR)" icon={<Plus className="w-3 h-3 mr-1"/>} className="p-button-text p-button-sm w-48 mt-1 text-xs" onClick={() => handleAddCondition(rIndex)} />
              </div>

              <div className="flex items-center gap-2 mt-2 bg-primary/10 p-2 rounded">
                <label className="text-sm font-semibold text-text whitespace-nowrap">Sonuç (Result):</label>
                <InputText placeholder="Örn: YURTİÇİ" value={rule.resultValue} onChange={(e) => {
                  const newRules = [...rules];
                  newRules[rIndex].resultValue = e.target.value;
                  setRules(newRules);
                }} className="w-full" />
              </div>
            </div>
          ))}

          <Button label="Yeni Kural Ekle" icon={<Plus className="w-4 h-4 mr-2"/>} className="p-button-outlined mt-2" onClick={handleAddRule} />
        </div>
      </div>
    </Dialog>
  );
};
